/**
 * Client-side email domain typo suggester ("did you mean?").
 *
 * Compares the typed domain against a list of the consumer email domains our
 * users actually use (by Levenshtein distance) and proposes a correction for
 * near-misses (e.g. `gmail.con` → `gmail.com`, `gmali.com` → `gmail.com`,
 * `yahol.com` → `yahoo.com`). It only *suggests* — callers offer one-click
 * acceptance, never block.
 *
 * The domain list is reality-driven, NOT a generic "popular domains" list:
 *  - Our learners are largely recent migrants / Spanish & multilingual
 *    speakers, so we include the localized variants they really use
 *    (`yahoo.es`, `hotmail.es`, `yahoo.fr`, `qq.com`). Listing these is what
 *    keeps us from *wrongly* "correcting" a valid `@yahoo.es` to `@yahoo.com`
 *    (it's only one edit away). This protection must be applied *symmetrically*
 *    across the major webmail providers — every listed `.com` provider also
 *    needs its Spanish/French variants listed (`outlook.es`/`live.es`/
 *    `hotmail.fr`/…), otherwise a valid `@outlook.es` gets mis-corrected to
 *    `outlook.com` — exactly the bug this list exists to avoid. Extend from
 *    real bounce/signup data, not by guessing.
 *  - We deliberately omit English-speaking-country variants we never see
 *    (`*.co.uk`, `*.co.nz`, US ISP domains), to keep the match surface small.
 *  - We do NOT do generic TLD rewriting (`.con`→`.com`): with many valid
 *    ccTLDs in our population (`.es`, `.mx`, `.fr`, `.co`), that would
 *    mis-correct real addresses. Whole-domain matching against the list below
 *    covers the consumer typos; the server-side MX check catches everything
 *    else (e.g. `@g.mail`, corporate-domain typos).
 *
 * Corporate/customer domains are intentionally absent — they're diverse and
 * handled by the MX check. If we later want to recognise them (e.g. to pre-fill
 * or to broaden suggestions), pull the live set from the database rather than
 * hardcoding here.
 */

const KNOWN_CONSUMER_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'yahoo.es',
  'yahoo.fr',
  'hotmail.com',
  'hotmail.es',
  'hotmail.fr',
  'outlook.com',
  'outlook.es',
  'outlook.fr',
  'icloud.com',
  'live.com',
  'live.es',
  'live.fr',
  'aol.com',
  'qq.com',
]

const DOMAIN_THRESHOLD = 2

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m

  let prev = Array.from({ length: n + 1 }, (_, i) => i)
  let curr = new Array<number>(n + 1)

  for (let i = 1; i <= m; i++) {
    curr[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    ;[prev, curr] = [curr, prev]
  }
  return prev[n]
}

/**
 * Returns a corrected email address if the domain looks like a typo of a known
 * consumer domain, otherwise null.
 */
export function suggestEmailCorrection(email: string): string | null {
  const trimmed = (email ?? '').trim()
  const atIndex = trimmed.lastIndexOf('@')
  if (atIndex <= 0 || atIndex === trimmed.length - 1) return null

  const local = trimmed.slice(0, atIndex)
  const domain = trimmed.slice(atIndex + 1).toLowerCase()

  // Exact match against a known domain — nothing to suggest. This is what
  // protects valid localized domains (yahoo.es, etc.) from being "corrected".
  if (KNOWN_CONSUMER_DOMAINS.includes(domain)) return null

  let best: string | null = null
  let bestDistance = DOMAIN_THRESHOLD + 1
  for (const candidate of KNOWN_CONSUMER_DOMAINS) {
    const distance = levenshtein(domain, candidate)
    if (distance < bestDistance) {
      bestDistance = distance
      best = candidate
    }
  }

  if (!best || bestDistance > DOMAIN_THRESHOLD) return null
  return `${local}@${best}`
}
